import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Read the persisted choice ('system' | 'light' | 'dark'); default to 'system'.
const readStoredTheme = () => {
  try {
    return localStorage.getItem('pixally-theme') || 'system';
  } catch (e) {
    return 'system';
  }
};

// Resolve a theme value to the applied scheme ('light' | 'dark').
const resolveTheme = (t) => {
  if (t === 'dark' || t === 'light') return t;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (e) {
    return 'light';
  }
};

export const ThemeProvider = ({ children }) => {
  // Initialize synchronously from storage/system so the first render already
  // matches the pre-paint script in index.html (no theme flash).
  const [theme, setTheme] = useState(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredTheme()));

  useEffect(() => {
    const root = window.document.documentElement;
    
    const applyTheme = (isDark) => {
      if (isDark) {
        root.classList.add('dark');
        setResolvedTheme('dark');
      } else {
        root.classList.remove('dark');
        setResolvedTheme('light');
      }
    };

    if (theme === 'system') {
      // Use system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches);

      // Listen for system theme changes
      const handler = (e) => applyTheme(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      applyTheme(theme === 'dark');
    }
  }, [theme]);

  const setThemeValue = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('pixally-theme', newTheme);
  };

  const toggleTheme = () => {
    if (theme === 'system') {
      // If system, switch to the opposite of current resolved theme
      setThemeValue(resolvedTheme === 'dark' ? 'light' : 'dark');
    } else {
      setThemeValue(theme === 'dark' ? 'light' : 'dark');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme: setThemeValue, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
